import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { salesApi, type SalesChannelStatus, type SalesSettings } from '../lib/salesApi';
import { AdminSalesHistory } from './AdminSalesHistory';
import { AdminSalesTargets } from './AdminSalesTargets';
import { AdminAnalytics } from './AdminAnalytics';

/**
 * Admin > Sales data: whether each channel's sales can be trusted yet, and
 * the settings that decide how much history is imported and what counts.
 *
 * The dashboard leaves out any channel whose data is missing for the range
 * being viewed; this is where an admin sees why, per channel, in one place.
 */

export function AdminSales() {
  const queryClient = useQueryClient();
  const { data, isLoading, error } = useQuery({ queryKey: ['sales-status'], queryFn: salesApi.status });
  const [result, setResult] = useState<{ ok: boolean; message: string } | null>(null);

  const refresh = () => void queryClient.invalidateQueries({ queryKey: ['sales-status'] });

  const sync = useMutation({
    mutationFn: (channelIds?: string[]) => salesApi.syncNow(channelIds),
    onSuccess: ({ results }) => {
      const failed = results.filter((r) => !r.ok);
      const imported = results.filter((r) => r.ok && !r.skipped);
      setResult(
        failed.length > 0
          ? { ok: false, message: failed.map((r) => `${r.channelName}: ${r.error}`).join(' · ') }
          : {
              ok: true,
              message:
                imported.length > 0
                  ? `Imported ${imported.map((r) => r.channelName).join(', ')}`
                  : 'Nothing was imported — see each row for why',
            },
      );
      refresh();
    },
    onError: (err: Error) => setResult({ ok: false, message: err.message }),
  });

  const restart = useMutation({
    mutationFn: salesApi.restartBackfill,
    onSuccess: () => {
      setResult({ ok: true, message: 'History will be re-imported from the start over the next runs' });
      refresh();
    },
    onError: (err: Error) => setResult({ ok: false, message: err.message }),
  });

  if (isLoading) return <div className="empty-state">Loading…</div>;
  if (error) return <div className="alert error">{(error as Error).message}</div>;
  if (!data) return null;

  return (
    <div>
      <div className="toolbar">
        <button className="primary" disabled={sync.isPending} onClick={() => sync.mutate(undefined)}>
          {sync.isPending ? 'Importing…' : 'Import now'}
        </button>
        <span className="muted small">
          Imports run automatically every 30 minutes through the working day and nightly at 03:50 Eastern.
        </span>
      </div>

      {result && (
        <div className={`alert ${result.ok ? 'ok' : 'error'}`} onClick={() => setResult(null)}>
          {result.message}
        </div>
      )}

      <div className="table-wrap">
        <table className="grid">
          <thead>
            <tr>
              <th>Channel</th>
              <th>Import</th>
              <th>Orders</th>
              <th>History from</th>
              <th>Last import</th>
              <th>Needs review</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {data.channels.map((channel) => (
              <ChannelRow
                key={channel.channelId}
                channel={channel}
                busy={sync.isPending || restart.isPending}
                onSync={() => sync.mutate([channel.channelId])}
                onRestart={() => {
                  if (
                    window.confirm(
                      `Re-import all of ${channel.name}'s history? Orders already imported are updated, not duplicated, ` +
                        'but it spends channel API calls over the next several runs.',
                    )
                  ) {
                    restart.mutate(channel.channelId);
                  }
                }}
              />
            ))}
          </tbody>
        </table>
      </div>

      <AdminSalesHistory channels={data.channels} />

      <AdminSalesTargets />

      <AdminAnalytics />

      <SalesSettingsForm settings={data.settings} channels={data.channels} onSaved={refresh} />
    </div>
  );
}

interface ImportStatus {
  label: string;
  tone: 'ok' | 'warn' | 'error' | '';
  detail: string | null;
}

/** One plain answer to "can I trust this channel's sales yet?" */
function importStatus(channel: SalesChannelStatus): ImportStatus {
  const state = channel.state;
  if (!channel.capabilities.orders) {
    return { label: 'not available yet', tone: '', detail: channel.capabilities.unsupportedReason };
  }
  if (!state) {
    return { label: 'not imported yet', tone: '', detail: 'Runs with the next import' };
  }
  if (!state.supported) {
    return { label: 'not importing', tone: 'warn', detail: state.unsupportedReason };
  }
  if (state.consecutiveFailures > 0) {
    return { label: `failing (${state.consecutiveFailures})`, tone: 'error', detail: state.lastError };
  }
  if (state.backfillStatus !== 'complete') {
    const pct = channel.coverage.backfillPct;
    return {
      label: pct === null ? 'importing history' : `importing history ${pct}%`,
      tone: 'warn',
      detail: 'Ranges before the imported history are left out of the dashboard until it finishes',
    };
  }
  if (channel.coverage.intervals > 1) {
    return { label: 'gap in history', tone: 'warn', detail: 'Part of the history was never imported — restart the backfill' };
  }
  return { label: 'up to date', tone: 'ok', detail: state.lastError };
}

const formatDate = (value: string | null) => (value ? new Date(value).toLocaleDateString() : '—');
const formatDateTime = (value: string | null) => (value ? new Date(value).toLocaleString() : 'never');

function ChannelRow({
  channel,
  busy,
  onSync,
  onRestart,
}: {
  channel: SalesChannelStatus;
  busy: boolean;
  onSync: () => void;
  onRestart: () => void;
}) {
  const status = importStatus(channel);
  const needsReview = channel.openIssues.conflict + channel.openIssues.warning;
  const canImport = channel.capabilities.orders && channel.enabled;

  return (
    <tr>
      <td>
        {channel.name}
        {!channel.enabled && <> <span className="badge">disabled</span></>}
        <div className="small muted">
          {channel.capabilities.recognitionBasis === 'payments' ? 'Counts payments received' : 'Counts order totals'}
          {channel.depositsExpected ? ' · takes deposits' : ''}
        </div>
      </td>
      <td>
        <span className={`badge ${status.tone}`}>{status.label}</span>
        {status.detail && <div className="small muted">{status.detail}</div>}
      </td>
      <td>
        {channel.orders.total}
        {channel.orders.total > 0 && <div className="small muted">{channel.orders.countable} counted as sales</div>}
      </td>
      <td className="small">{formatDate(channel.coverage.earliest)}</td>
      <td className="small muted">{formatDateTime(channel.state?.lastSuccessAt ?? null)}</td>
      <td>
        {needsReview > 0 ? (
          <span className={`badge ${channel.openIssues.conflict > 0 ? 'error' : 'warn'}`}>
            {needsReview} order{needsReview === 1 ? '' : 's'}
          </span>
        ) : (
          <span className="muted small">none</span>
        )}
      </td>
      <td className="nowrap">
        {canImport && (
          <>
            <button disabled={busy} onClick={onSync}>
              Import
            </button>{' '}
            <button className="link" disabled={busy} onClick={onRestart}>
              Restart history
            </button>
          </>
        )}
      </td>
    </tr>
  );
}

function SalesSettingsForm({
  settings,
  channels,
  onSaved,
}: {
  settings: SalesSettings;
  channels: SalesChannelStatus[];
  onSaved: () => void;
}) {
  const [form, setForm] = useState<SalesSettings>(settings);
  const [result, setResult] = useState<{ ok: boolean; message: string } | null>(null);
  const [reviewChanged, setReviewChanged] = useState(false);

  useEffect(() => setForm(settings), [settings]);

  const save = useMutation({
    mutationFn: () => salesApi.saveSettings(form),
    onSuccess: ({ settings: saved }) => {
      setReviewChanged(JSON.stringify(saved.review) !== JSON.stringify(settings.review) || reviewChanged);
      setForm(saved);
      setResult({ ok: true, message: 'Saved' });
      onSaved();
    },
    onError: (err: Error) => setResult({ ok: false, message: err.message }),
  });

  const reevaluate = useMutation({
    mutationFn: salesApi.reevaluate,
    onSuccess: (r) => {
      setReviewChanged(false);
      setResult(
        r.failed > 0
          ? { ok: false, message: `${r.evaluated} re-evaluated, ${r.failed} failed: ${r.firstError}` }
          : { ok: true, message: `${r.evaluated} orders re-evaluated` },
      );
      onSaved();
    },
    onError: (err: Error) => setResult({ ok: false, message: err.message }),
  });

  const set = <K extends keyof SalesSettings>(key: K, value: SalesSettings[K]) =>
    setForm((current) => ({ ...current, [key]: value }));
  const setReview = <K extends keyof SalesSettings['review']>(key: K, value: SalesSettings['review'][K]) =>
    setForm((current) => ({ ...current, review: { ...current.review, [key]: value } }));
  const setInsight = <K extends keyof SalesSettings['insights']>(key: K, value: SalesSettings['insights'][K]) =>
    setForm((current) => ({ ...current, insights: { ...current.insights, [key]: value } }));
  /** Shares are stored as fractions and edited as percentages. */
  const percent = (value: number) => Math.round(value * 1000) / 10;

  const toggleDeposit = (channelId: string, on: boolean) =>
    set(
      'depositChannelIds',
      on ? [...new Set([...form.depositChannelIds, channelId])] : form.depositChannelIds.filter((id) => id !== channelId),
    );

  const siteChannels = channels.filter((c) => c.capabilities.recognitionBasis === 'payments');
  const busy = save.isPending || reevaluate.isPending;

  return (
    <div className="card" style={{ maxWidth: 680, marginTop: 16 }}>
      <h3 style={{ marginTop: 0 }}>Sales settings</h3>

      {result && (
        <div className={`alert ${result.ok ? 'ok' : 'error'}`} onClick={() => setResult(null)}>
          {result.message}
        </div>
      )}
      {reviewChanged && !reevaluate.isPending && (
        <div className="alert warn small">
          Review settings changed. Orders already imported keep their old findings until they are re-evaluated.{' '}
          <button className="link" onClick={() => reevaluate.mutate()}>
            Re-evaluate all orders now
          </button>
        </div>
      )}

      <div className="field-row">
        <div className="field" style={{ width: 160 }}>
          <label htmlFor="historyYears">Years of history</label>
          <input
            id="historyYears"
            type="number"
            min={1}
            max={10}
            value={form.historyYears}
            onChange={(e) => set('historyYears', Number(e.target.value))}
          />
        </div>
        <div className="field" style={{ flex: 1 }}>
          <label>&nbsp;</label>
          <span className="small muted">
            Each channel imports this far back, or as far as its API allows if that is less. Raising it resumes the
            import further back; lowering it hides older sales but keeps them.
          </span>
        </div>
      </div>

      <div className="field">
        <label>
          <input
            type="checkbox"
            checked={form.pruneOutsideHistory}
            onChange={(e) => set('pruneOutsideHistory', e.target.checked)}
          />{' '}
          Delete sales older than the history setting
        </label>
        <span className="small muted">
          Runs daily at 06:45 Eastern. Deleted history can only come back by re-importing it, and some channels cannot
          go back as far as others.
        </span>
      </div>

      <div className="field">
        <label>Channels that take deposits</label>
        {siteChannels.map((channel) => (
          <div key={channel.channelId}>
            <label>
              <input
                type="checkbox"
                checked={form.depositChannelIds.includes(channel.channelId)}
                onChange={(e) => toggleDeposit(channel.channelId, e.target.checked)}
              />{' '}
              {channel.name}
            </label>
          </div>
        ))}
        <span className="small muted">
          Where deposits are normal, a part-paid open order waits {form.review.depositGraceDays} days before it is
          flagged; everywhere else, {form.review.partialPaymentGraceDays}.
        </span>
      </div>

      <h4>Review thresholds</h4>
      <div className="field-row">
        <NumberField
          id="partialPaymentGraceDays"
          label="Days before a part-paid or unpaid order is flagged"
          value={form.review.partialPaymentGraceDays}
          onChange={(v) => setReview('partialPaymentGraceDays', v)}
        />
        <NumberField
          id="depositGraceDays"
          label="…where deposits are taken"
          value={form.review.depositGraceDays}
          onChange={(v) => setReview('depositGraceDays', v)}
        />
      </div>
      <div className="field-row">
        <NumberField
          id="toleranceMinor"
          label="Rounding tolerance ($)"
          value={form.review.toleranceMinor / 100}
          step={0.01}
          onChange={(v) => setReview('toleranceMinor', Math.round(v * 100))}
        />
        <NumberField
          id="longPaymentDays"
          label="Days to first payment before it is unusual"
          value={form.review.longPaymentDays}
          onChange={(v) => setReview('longPaymentDays', v)}
        />
        <NumberField
          id="duplicateWindowMinutes"
          label="Minutes apart for a duplicate payment"
          value={form.review.duplicateWindowMinutes}
          onChange={(v) => setReview('duplicateWindowMinutes', v)}
        />
      </div>
      <div className="field-row">
        <NumberField
          id="openOrderStaleDays"
          label="Days after which an “open” order leaves the open-orders pipeline"
          value={form.openOrderStaleDays}
          onChange={(v) => set('openOrderStaleDays', v)}
        />
      </div>

      <h4>Insights</h4>
      <p className="small muted" style={{ marginTop: 0 }}>
        When an insight is worth showing. Every rule needs both a real-sized change and enough sales to be sure of it; raise these if
        the feed is noisy, lower them if it misses things.
      </p>
      <div className="field-row">
        <NumberField id="deadStockDays" label="Days without a sale before a live listing is “not selling”" value={form.deadStockDays} onChange={(v) => set('deadStockDays', v)} />
        <NumberField id="topSellers" label="Best sellers the listing checks look at" value={form.insights.topSellers} onChange={(v) => setInsight('topSellers', v)} />
      </div>
      <div className="field-row">
        <NumberField id="surgeMultiple" label="Surge: times the usual rate" step={0.1} value={form.insights.surgeMultiple} onChange={(v) => setInsight('surgeMultiple', v)} />
        <NumberField id="surgeMinUnits" label="…and at least this many units in 14 days" value={form.insights.surgeMinUnits} onChange={(v) => setInsight('surgeMinUnits', v)} />
      </div>
      <div className="field-row">
        <NumberField id="trendChangePct" label="Channel up/down on last year by (%)" value={percent(form.insights.trendChangePct)} onChange={(v) => setInsight('trendChangePct', v / 100)} />
        <NumberField id="trendMinOrders" label="…with at least this many orders last year (28 days)" value={form.insights.trendMinOrders} onChange={(v) => setInsight('trendMinOrders', v)} />
        <NumberField id="mixShiftPts" label="Site mix shift (points)" value={percent(form.insights.mixShiftPts)} onChange={(v) => setInsight('mixShiftPts', v / 100)} />
      </div>
      <div className="field-row">
        <NumberField id="refundMultiple" label="Refunds: times the usual rate" step={0.1} value={form.insights.refundMultiple} onChange={(v) => setInsight('refundMultiple', v)} />
        <NumberField id="refundMinMinor" label="…and at least ($ refunded)" value={form.insights.refundMinMinor / 100} onChange={(v) => setInsight('refundMinMinor', Math.round(v * 100))} />
        <NumberField id="feeJumpPts" label="Fee rate jump (points)" step={0.5} value={percent(form.insights.feeJumpPts)} onChange={(v) => setInsight('feeJumpPts', v / 100)} />
      </div>
      <div className="field-row">
        <NumberField id="openOrderAgingDays" label="Open orders ageing after (days)" value={form.insights.openOrderAgingDays} onChange={(v) => setInsight('openOrderAgingDays', v)} />
        <NumberField id="reviewBacklogPct" label="Review backlog at (% of 90 days’ sales)" step={0.5} value={percent(form.insights.reviewBacklogPct)} onChange={(v) => setInsight('reviewBacklogPct', v / 100)} />
        <NumberField id="forecastMissPct" label="Forecast miss at (%)" value={percent(form.insights.forecastMissPct)} onChange={(v) => setInsight('forecastMissPct', v / 100)} />
      </div>

      <h4>Import pacing</h4>
      <div className="field-row">
        <NumberField
          id="intradayBackfillMinutes"
          label="History minutes per daytime run"
          value={form.intradayBackfillMinutes}
          onChange={(v) => set('intradayBackfillMinutes', v)}
        />
        <NumberField
          id="nightlyBackfillMinutes"
          label="History minutes per nightly run"
          value={form.nightlyBackfillMinutes}
          onChange={(v) => set('nightlyBackfillMinutes', v)}
        />
        <NumberField
          id="staleAfterMinutes"
          label="Minutes before an import counts as stale"
          value={form.staleAfterMinutes}
          onChange={(v) => set('staleAfterMinutes', v)}
        />
      </div>

      <button className="primary" disabled={busy} onClick={() => save.mutate()}>
        {save.isPending ? 'Saving…' : 'Save'}
      </button>
    </div>
  );
}

function NumberField({
  id,
  label,
  value,
  step = 1,
  onChange,
}: {
  id: string;
  label: string;
  value: number;
  step?: number;
  onChange: (value: number) => void;
}) {
  return (
    <div className="field" style={{ flex: 1 }}>
      <label htmlFor={id}>{label}</label>
      <input id={id} type="number" min={0} step={step} value={value} onChange={(e) => onChange(Number(e.target.value))} />
    </div>
  );
}
